import * as React from 'react';
import { Table, HiddenInput, RadioButton, CustomButton, ButtonType } from "soprasteria-ui-library";
import { IRiskPotReportTreatment } from '../../../models/IRiskPotReport';
import { dateTimeToDateString } from '../../../helpers/DateHelper';
import { RiskPotReportTreatmentReportingApi } from '../../../api/RiskPotReportTreatmentReportingApi';
import { useAuth } from '../../../App';
import { EditRiskPotReportTreatmentModal } from './EditRiskPotReportTreatmentModal';
import { formatCurrency } from '../../../helpers/CurrencyHelper';
import { faExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IRiskPotReportTreatmentsTable {
    treatments: IRiskPotReportTreatment[];
    canAssessTreatments: () => boolean;
    riskPotReportStartDate: Date;
    riskPotReportEndDate: Date;
    getRiskPotReport: () => void;
    riskPotReportinvoicedAmount: number;
    getPreAssessment: (preAssessmentId: number) => void;
}

interface ILoading {
    isLoading: boolean;
    treatmentId: number;
}

export const RiskPotReportTreatmentsTable: React.FunctionComponent<IRiskPotReportTreatmentsTable> = ({ treatments, canAssessTreatments, getRiskPotReport, riskPotReportStartDate, riskPotReportEndDate, riskPotReportinvoicedAmount, getPreAssessment }) => {

    const auth = useAuth();
    const api = new RiskPotReportTreatmentReportingApi(auth);
    const [isLoading, setIsLoading] = React.useState<ILoading>();
    const [selectedTreatmentToEdit, setSelectedTreatmentToEdit] = React.useState<IRiskPotReportTreatment>();

    const tooltipId = "emergencyReportComment";
    React.useEffect(() => {

        var myWindow: any = window;

        myWindow.$(`#${tooltipId}`).tooltip();

    }, [treatments])

    const removeTreatment = (patientTreatmentId: number) => {
        const confirmed = window.confirm("Är du säker på att du vill ta bort åtgärden?");
        if (confirmed) {
            setIsLoading({ isLoading: true, treatmentId: patientTreatmentId });
            api.removeTreatment(patientTreatmentId, () => {
                getRiskPotReport();
            }, undefined, () => setIsLoading({ isLoading: false, treatmentId: patientTreatmentId }));
        }
    }

    const creditTreatment = (riskPotReportPatientTreatmentId: number, patientTreatmentAmount: number) => {
        const confirmationText = riskPotReportinvoicedAmount - patientTreatmentAmount < 0
            ? "Då kreditering av den här åtgärden leder till att det fakturerbara beloppet understiger självrisken så kommer alla fakturerade åtgärder att krediteras."
            : "Är du säker på att du vill kreditera åtgärden?";
        const confirmed = window.confirm(confirmationText);

        if (confirmed) {
            api.creditTreatment(riskPotReportPatientTreatmentId, () => {
                getRiskPotReport();
            })
        }
    }

    const canAnyTreatmentBeRemoved = () => {
        return treatments.some((treatment) => treatment.canBeRemoved);
    }

    const canAnyTreatmentBeEdited = () => {
        return treatments.some((treatment) => treatment.canBeEdited);
    }

    const canAnyTreatmentBeCredited = () => {
        return treatments.some((treatment) => treatment.isCreditable);
    }

    const anyTreatmentHasRiskPotReportPreAssessmentId = () => {
        return treatments.some((treatment) => treatment.riskPotReportPreAssessmentId !== null);
    }

    return (
        <>
            <Table>
                <thead>
                    <tr>
                        {
                            treatments.some(t => t.existsInEmergencyTreatmentComment != undefined) ?
                                <th style={{ border: "none" }}></th>
                                : null
                        }
                        <th>Tand/käke</th>
                        <th>Tandyta</th>
                        <th>Åtgärdskod</th>
                        <th>Antal</th>
                        <th className="text-right">á-pris</th>
                        <th className="text-right">Summa</th>
                        <th>Behandlingsdatum</th>
                        <th>Status</th>
                        {
                            canAssessTreatments() ?
                                <th style={{ width: "10%" }}>Godkänd</th>
                                : null
                        }
                        {
                            canAnyTreatmentBeRemoved() || canAnyTreatmentBeEdited() || canAnyTreatmentBeCredited() || anyTreatmentHasRiskPotReportPreAssessmentId()
                                ? <th></th>
                                : null
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        treatments && treatments.length > 0 ?
                            treatments.map((treatment, key) =>
                                <tr key={key}>
                                    {
                                        treatments.some(t => t.existsInEmergencyTreatmentComment != undefined) ?
                                            treatment.existsInEmergencyTreatmentComment ?
                                                <td style={{ border: "none", backgroundColor: "white" }} title={treatment.existsInEmergencyTreatmentComment} data-toggle="tooltip"
                                                    data-placement="top" id={tooltipId}><FontAwesomeIcon style={{ color: "red" }} icon={faExclamation} /></td>
                                                : <td style={{ border: "none", backgroundColor: "white" }} />
                                            : null
                                    }
                                    <td>{treatment.toothCode?.code}</td>
                                    <td>{treatment.toothSurface}</td>
                                    <td>{treatment.code}</td>
                                    <td>{treatment.quantity}</td>
                                    <td style={{ width: "7%" }} className="text-right text-nowrap">{formatCurrency(treatment.price)}</td>
                                    <td style={{ width: "7%" }} className="text-right text-nowrap">{formatCurrency(treatment.amount ?? 0)}</td>
                                    <td>{dateTimeToDateString(treatment.treatmentDate, "Ej utförd")}</td>
                                    <td style={{ width: "10%" }}>{treatment.status}</td>
                                    {
                                        canAssessTreatments() ?
                                            treatment.needAssessment && treatment.patientTreatmentId ?
                                                <td style={{ width: "20%" }}>
                                                    <HiddenInput name={`treatments[${key}].patientTreatmentId`} value={treatment.patientTreatmentId.toString()} />
                                                    <RadioButton name={`treatments[${key}].isApproved`} id={`approved${key}`} label="Ja" value="true" inlineLabel required />
                                                    <RadioButton name={`treatments[${key}].isApproved`} id={`declined${key}`} label="Nej" value="false" inlineLabel required />
                                                </td>
                                                : <td></td>
                                            : null
                                    }
                                    {
                                        canAnyTreatmentBeRemoved() || canAnyTreatmentBeEdited() || canAnyTreatmentBeCredited() || anyTreatmentHasRiskPotReportPreAssessmentId()
                                            ? <td style={{ maxWidth: "10em" }} className="text-left">
                                                {
                                                    treatment.canBeRemoved ?
                                                        <CustomButton
                                                            buttonType={ButtonType.delete}
                                                            buttonText=""
                                                            onClick={() => removeTreatment(treatment.patientTreatmentId ?? 0)}
                                                            isLoading={isLoading?.treatmentId === treatment.patientTreatmentId && isLoading?.isLoading} />
                                                        : null
                                                }
                                                {
                                                    treatment.canBeEdited ?
                                                        <CustomButton
                                                            buttonType={ButtonType.editAlt}
                                                            buttonText=""
                                                            onClick={() => setSelectedTreatmentToEdit(treatment)}
                                                            data-toggle="modal"
                                                            data-target="#edit-risk-pot-report-treatment-modal" />
                                                        : null
                                                }
                                                {
                                                    treatment.isCreditable ?
                                                        <CustomButton
                                                            buttonType={ButtonType.credit}
                                                            buttonText=""
                                                            onClick={() => creditTreatment(treatment.riskPotReportPatientTreatmentId, treatment.amount ?? 0)} />
                                                        : null
                                                }
                                                {
                                                    treatment.riskPotReportPreAssessmentId !== null ?
                                                        <CustomButton
                                                            buttonType={ButtonType.general}
                                                            buttonText="Se FHB"
                                                            onClick={() => getPreAssessment(treatment.riskPotReportPreAssessmentId ?? 0)} />
                                                        : null
                                                }
                                            </td>
                                            : null
                                    }
                                </tr>
                            )
                            :
                            <tr><td colSpan={10}>Det finns inga registrerade åtgärdskoder</td></tr>
                    }
                </tbody>
            </Table>
            <EditRiskPotReportTreatmentModal riskPotReportStartDate={riskPotReportStartDate} riskPotReportEndDate={riskPotReportEndDate} getRiskPotReport={getRiskPotReport} treatment={selectedTreatmentToEdit} setSelectedTreatmentToEdit={setSelectedTreatmentToEdit} />
        </>
    )
}

